import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Pricing.css';
import { FiCheck, FiArrowRight, FiInfo } from 'react-icons/fi';
import PublicNavbar from '../components/layout/PublicNavbar';
import { useAuth } from '../context/AuthContext';
import subscriptionService from '../services/subscription.service';

const Pricing = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [loadingUpgrade, setLoadingUpgrade] = useState(false);
    const [upgradeError, setUpgradeError] = useState('');
    // Base pricing for CotizaPlus
    const annualPrice = 239880;
    const monthlyEquivalent = Math.round(annualPrice / 12);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(price);
    };

    const handleCotizaPlusClick = async () => {
        if (!isAuthenticated) {
            navigate('/register');
            return;
        }

        const suscripcion = user?.empresa?.suscripcion;
        if (suscripcion?.plan?.aplicacion === 'COTIZAPLUS' && suscripcion?.estado === 'ACTIVA') {
            // Ya lo tiene
            return;
        }

        try {
            setLoadingUpgrade(true);
            setUpgradeError('');
            const res = await subscriptionService.upgradePlan('cotizaplus');
            if (res.pago_id) {
                navigate(`/payment/checkout/${res.pago_id}`);
            }
        } catch (error) {
            console.error('Error al solicitar upgrade:', error);
            setUpgradeError(error.response?.data?.error || 'No se pudo procesar la solicitud. Intenta nuevamente.');
        } finally {
            setLoadingUpgrade(false);
        }
    };

    // Determinar estado actual de CotizaPlus para el usuario autenticado
    const suscripcion = user?.empresa?.suscripcion;
    const hasCotizaPlus = suscripcion?.plan?.aplicacion === 'COTIZAPLUS' && suscripcion?.estado === 'ACTIVA';

    return (
        <>
            {!isAuthenticated && <PublicNavbar />}
            <div className="pricing-page" style={{ paddingTop: isAuthenticated ? '0px' : '60px' }}>
                <div className="pricing-header">
                    <h2>Elige las herramientas que tu negocio necesita</h2>
                    <p>Arma tu suite ideal. <span className="highlight-text">Todos los módulos incluyen 30 días de prueba gratis</span>. Cancela cuando quieras.</p>
                </div>

                <div className="pricing-cards-container">
                    {/* CotizaPlus Card */}
                    <div className="pricing-card pro-card popular">
                        <div className="popular-badge">Disponible Ahora</div>
                        <div className="card-top">
                            <div className="app-logo-area">
                                <div className="app-icon-mini quote-icon">✦</div>
                            </div>
                            <h3>CotizaPlus</h3>
                            <div className="price-tag">
                                <span className="amount">{formatPrice(monthlyEquivalent)}</span>
                                <span className="period">/ mes</span>
                            </div>
                            <div className="savings-alert" style={{ color: '#94a3b8', background: 'transparent', padding: '0', marginTop: '4px' }}>
                                Facturado anualmente ({formatPrice(annualPrice)})
                            </div>
                            <p className="card-description">Automatización inteligente de cotizaciones. Impruime PDFs, gestiona clientes y links web de pago.</p>
                        </div>

                        <ul className="features-list">
                            <li><FiCheck className="feature-icon" /> Cotizaciones y Clientes ilimitados</li>
                            <li><FiCheck className="feature-icon" /> Módulo de Autoatención pública</li>
                            <li><FiCheck className="feature-icon" /> Generación de PDFs formales</li>
                            <li><FiCheck className="feature-icon" /> Enlaces a WhatsApp directos</li>
                        </ul>

                        {upgradeError && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '10px' }}>{upgradeError}</p>}

                        {hasCotizaPlus ? (
                            <button className="btn-action" disabled style={{ background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed', border: '1px solid #e2e8f0' }}>
                                <FiCheck className="btn-icon" /> Ya tienes este plan
                            </button>
                        ) : (
                            <button className="btn-action primary" onClick={handleCotizaPlusClick} disabled={loadingUpgrade}>
                                {loadingUpgrade ? 'Procesando...' : (
                                    <>
                                        {isAuthenticated ? 'Adquirir Plan' : 'Iniciar Prueba Gratis'} <FiArrowRight className="btn-icon" />
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {/* CRM Card */}
                    <div className="pricing-card future-card">
                        <div className="card-top">
                            <div className="app-logo-area">
                                <div className="app-icon-mini crm-icon">★</div>
                            </div>
                            <h3>CRM Plus</h3>
                            <div className="price-tag">
                                <span className="amount">Próximamente</span>
                            </div>
                            <p className="card-description">Gestión de relaciones comerciales, embudo de ventas y seguimiento automático de leads.</p>
                        </div>

                        <ul className="features-list">
                            <li className="faded"><FiCheck className="feature-icon" /> Embudo de Ventas (Pipeline)</li>
                            <li className="faded"><FiCheck className="feature-icon" /> Seguimiento de Leads (Follow-ups)</li>
                            <li className="faded"><FiCheck className="feature-icon" /> Recordatorios de tareas</li>
                            <li className="faded"><FiCheck className="feature-icon" /> Calificación de posibles clientes</li>
                        </ul>

                        <button className="btn-action disabled" disabled>
                            En Desarrollo
                        </button>
                    </div>

                    {/* Inventory Card */}
                    <div className="pricing-card future-card">
                        <div className="card-top">
                            <div className="app-logo-area">
                                <div className="app-icon-mini inventory-icon">❖</div>
                            </div>
                            <h3>Inventario Plus</h3>
                            <div className="price-tag">
                                <span className="amount">Próximamente</span>
                            </div>
                            <p className="card-description">Control total de stock, alertas de reposición, multi-bodega y gestión de proveedores operacionales.</p>
                        </div>

                        <ul className="features-list">
                            <li className="faded"><FiCheck className="feature-icon" /> Control de Stock Multibodega</li>
                            <li className="faded"><FiCheck className="feature-icon" /> Alertas de Stock Crítico</li>
                            <li className="faded"><FiCheck className="feature-icon" /> Órdenes de Compra Automatizadas</li>
                            <li className="faded"><FiCheck className="feature-icon" /> Reportes de rotación de activos</li>
                        </ul>

                        <button className="btn-action disabled" disabled>
                            En Desarrollo
                        </button>
                    </div>

                    {/* Bundle / Suite Card */}
                    <div className="pricing-card suite-card">
                        <div className="popular-badge" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>La Mejor Opción</div>
                        <div className="card-top">
                            <div className="app-logo-area">
                                <div className="app-icon-mini suite-icon" style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)' }}>🚀</div>
                            </div>
                            <h3>Suite IdevPlus</h3>
                            <div className="price-tag">
                                <span className="amount" style={{ fontSize: '2rem' }}>Arma tu Pack</span>
                            </div>
                            <p className="card-description">Lleva 2 o más módulos y obtén un <strong>descuento especial adicional</strong> sobre el valor total.</p>
                        </div>

                        <ul className="features-list">
                            <li><FiCheck className="feature-icon" /> Combina CotizaPlus + CRM Plus</li>
                            <li><FiCheck className="feature-icon" /> Combina CRM Plus + Inventario</li>
                            <li><FiCheck className="feature-icon" /> <strong>O lleva la Suite Completa</strong></li>
                            <li><FiCheck className="feature-icon" /> Integración nativa entre módulos</li>
                        </ul>

                        <button className="btn-action" onClick={() => navigate('/contacto')} style={{ background: '#1e293b', color: 'white' }}>
                            Cotizar mi Pack <FiArrowRight className="btn-icon" />
                        </button>
                    </div>
                </div>

                <div className="pricing-faq">
                    <div className="faq-box">
                        <FiInfo size={20} className="info-icon" />
                        <div>
                            <strong>¿Cómo funciona el periodo de prueba?</strong>
                            <p>Cada módulo incluye 30 días o un límite de uso (ej. 3 cotizaciones) totalmente gratuito para que lo evalúes. No pedimos tarjeta de crédito para iniciar.</p>
                        </div>
                    </div>
                    <p className="small-disclaimer">* Los pagos con tarjeta/transferencia en la plataforma incluyen un recargo de comisiones del operador (Flow.cl) del ~3% + IVA al momento del pago final.</p>
                </div>
            </div>
        </>
    );
};

export default Pricing;
